import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeedbackCategories } from './ReportIssueContext';
import { IssueCategory } from './types';
import PageTransition from '../../../components/PageTransition/PageTransition';
import { Box, Button, Divider, Heading, Text } from '@indeed/ifl-components';
import { ChevronRight } from '@indeed/ifl-icons';
import useGettext from '../../../util/GettextFactory';

/**
 * Route wrapper for CategorySelectionStep
 * Handles navigation and proctor data fetching
 */
const CategorySelectionStep: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const gt = useGettext();
    const feedbackCategories = useFeedbackCategories();

    const handleCategorySelect = (category: IssueCategory) => {
        void navigate({
            pathname: `/auto-apply-setup/report-issue/${category}`,
            search: searchParams.toString(),
        });
    };

    return (
        <PageTransition pageKey="auto-apply-report-issue-category">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    padding: 3,
                }}
            >
                <Box
                    sx={{
                        margin: '0 auto',
                        inlineSize: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Heading level={4} sx={{ marginBlockEnd: 0 }}>
                            {gt.gettext('What is the issue?')}
                        </Heading>
                        <Text level={3} variant={'secondary'}>
                            {gt.gettext(
                                'Your feedback will help us fix issues and improve your experience across Indeed.',
                            )}
                        </Text>
                    </Box>

                    {/* Issue Categories */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0,
                        }}
                    >
                        {feedbackCategories.map((category) => (
                            <React.Fragment key={category.id}>
                                <Button
                                    variant="text"
                                    size="lg"
                                    onClick={() => handleCategorySelect(category.id)}
                                    sx={{
                                        justifyContent: 'space-between',
                                        paddingBlock: 5,
                                        paddingInline: 0,
                                        textAlign: 'start',
                                    }}
                                >
                                    <Text level={3} sx={{ fontWeight: 400 }}>
                                        {category.title}
                                    </Text>
                                    <ChevronRight color={'foreground.strong'} aria-hidden="true" />
                                </Button>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default CategorySelectionStep;
